import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  formatDateTimeShort,
  GroupDescriptionType,
  SampleCommentType,
  Table,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { FC, useMemo } from 'react';
import translations from './SampleCommentsTable.translations.json';
import { Flex } from 'antd';
import styled from 'styled-components';
import { colorTextQuaternary } from '@atlas-frontend/design-tokens';
import { getCommentDescriptionLabel } from './getCommentDescriptionLabel';

const { addTranslations, useTranslation } = i18nNsFactory(
  'sample-comments-view-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<SampleCommentType>();

export const SampleCommentsViewTable: FC<{
  comments: SampleCommentType[];
  descriptions: GroupDescriptionType[];
  groupId?: number;
}> = ({ comments, descriptions, groupId }) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () =>
      [
        groupId !== undefined &&
          columnHelper.accessor('sampleDataObjectDescriptionId', {
            header: t('column.groupOrInput'),
            cell: (props) => {
              if (props.renderValue() == null) {
                return t('groupOrInputOptions.group');
              }

              return (
                <TextEllipsis>
                  {getCommentDescriptionLabel(props.row.original)}
                </TextEllipsis>
              );
            },
            filterFn: 'includesOption',
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              minWidth: 150,
            },
          }),
        columnHelper.accessor('createdBy.username', {
          header: t('column.user'),
          meta: {
            width: '20ch',
          },
        }),
        columnHelper.accessor('comments', {
          header: () => t('column.comment'),
          meta: {
            minWidth: 150,
          },
          enableSorting: false,
        }),
        columnHelper.accessor('createdAt', {
          header: t('column.createdAt'),
          cell: (props) => formatDateTimeShort(props.renderValue()) ?? '',
          filterFn: 'formattedDate',
          meta: {
            width: 200,
          },
        }),
        columnHelper.accessor('modifiedAt', {
          header: t('column.modifiedAt'),
          cell: (props) => formatDateTimeShort(props.renderValue()) ?? '',
          filterFn: 'formattedDate',
          meta: {
            width: 200,
          },
        }),
      ].filter((column) => !!column) as ColumnDef<SampleCommentType, any>[],
    [groupId, t],
  );

  return (
    <TableWrapper align="end" gap="middle" vertical>
      <Table
        name="comments"
        data={comments}
        columns={columns}
        defaultSort={[{ id: 'createdAt', desc: true }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
        rowHtmlAttributes={(comment) => ({
          className: isActiveDescription(comment, descriptions)
            ? ''
            : 'inactive',
        })}
      />
    </TableWrapper>
  );
};

const isActiveDescription = (
  comment: SampleCommentType,
  descriptions: GroupDescriptionType[],
) => {
  if (comment.id === undefined || comment.id === null) {
    return true;
  }

  const description = descriptions.find(
    (description) =>
      description.id === comment.sampleDataObjectDescriptionId ||
      comment.sampleDataObjectDescriptionId === null,
  );
  if (!description) return false;

  return description.activeVersionId !== null;
};

const TableWrapper = styled(Flex)`
  .inactive * {
    color: ${colorTextQuaternary};
  }
`;
